import type { ReactNode } from 'react';
import {
  OPPORTUNITY_STATUSES,
  OPPORTUNITY_STATUS_COLORS,
  OPPORTUNITY_STATUS_LABELS,
  type LocationSummary,
  type OpportunityFilterState,
  type OpportunityStatus,
} from '../types';
import { daysAgo, toDateInput } from '../format';
import { TagFilter, type TagOption } from './TagFilter';
import { DateRangeField, FilterPanel, SwitchField } from './FilterPanel';

interface Props {
  filters: OpportunityFilterState;
  onChange: (next: OpportunityFilterState) => void;
  onReset?: () => void;
  /** The saved-views menu, shown in the panel header. */
  views?: ReactNode;
  locations: LocationSummary[];
  people: { userId: string | null; userName: string | null; locationId: string; total: number }[];
  pipelines: { id: string; name: string | null; locationId: string }[];
  stages: { id: string; name: string | null; pipelineId: string; position: number }[];
  tagOptions?: TagOption[];
}

const PRESETS: { label: string; days: number }[] = [
  { label: '30d', days: 29 },
  { label: '90d', days: 89 },
  { label: '12m', days: 364 },
];

export function OpportunityFilterBar({
  filters,
  onChange,
  onReset,
  views,
  locations,
  people,
  pipelines,
  stages,
  tagOptions = [],
}: Props) {
  const today = toDateInput(new Date());
  const visibleLocations = filters.activeClientsOnly
    ? locations.filter((location) => location.isActiveClient)
    : locations;

  /*
   * Agencies build sub-accounts from snapshots, so the same pipeline and stage
   * names repeat once per sub-account. Listing every copy made "New Lead"
   * appear dozens of times, and choosing one filtered to a single
   * sub-account's copy. Options are grouped by name instead: choosing a name
   * selects every pipeline or stage with that name in scope.
   */
  const pipelineGroups = groupByName(pipelines, (pipeline) => pipeline.name ?? pipeline.id);
  const selectedPipelineKey =
    pipelineGroups.find((group) => group.ids.some((id) => filters.pipelineIds.includes(id)))?.key ?? '';

  // Stages follow the pipeline choice.
  const stagesInScope = filters.pipelineIds.length
    ? stages.filter((stage) => filters.pipelineIds.includes(stage.pipelineId))
    : stages;
  const stageGroups = groupByName(stagesInScope, (stage) => stage.name ?? stage.id).sort(
    (a, b) =>
      Math.min(...a.items.map((stage) => stage.position)) -
        Math.min(...b.items.map((stage) => stage.position)) || a.key.localeCompare(b.key),
  );
  const selectedStageKey =
    stageGroups.find((group) => group.ids.some((id) => filters.stageIds.includes(id)))?.key ?? '';

  function toggleStatus(status: OpportunityStatus) {
    const next = filters.statuses.includes(status)
      ? filters.statuses.filter((item) => item !== status)
      : [...filters.statuses, status];
    onChange({ ...filters, statuses: next });
  }

  const activeCount =
    (filters.locationIds.length ? 1 : 0) +
    (filters.userIds.length ? 1 : 0) +
    (filters.pipelineIds.length ? 1 : 0) +
    (filters.stageIds.length ? 1 : 0) +
    (filters.statuses.length ? 1 : 0) +
    (filters.search.trim() ? 1 : 0) +
    (filters.activeClientsOnly ? 0 : 1);
  const moreActiveCount =
    (filters.appointmentState !== 'any' ? 1 : 0) +
    (filters.appointmentFrom || filters.appointmentTo ? 1 : 0) +
    (filters.minLeadScore ? 1 : 0) +
    (filters.tags.length ? 1 : 0) +
    (filters.includeDeleted ? 1 : 0);

  const more = (
    <>
      <div className="field">
        <label htmlFor="opp-appt-state">Appointments</label>
        <select
          id="opp-appt-state"
          value={filters.appointmentState}
          onChange={(event) =>
            onChange({
              ...filters,
              appointmentState: event.target.value as OpportunityFilterState['appointmentState'],
            })
          }
        >
          <option value="any">Any</option>
          <option value="has">Has an appointment</option>
          <option value="none">No appointment booked</option>
          <option value="upcoming">Has one upcoming</option>
          <option value="past">Has had one</option>
        </select>
      </div>

      <DateRangeField
        id="opp-appt-from"
        label="Appointment between"
        from={filters.appointmentFrom}
        to={filters.appointmentTo}
        clearable
        onChange={(appointmentFrom, appointmentTo) => onChange({ ...filters, appointmentFrom, appointmentTo })}
      />

      <div className="field">
        <label htmlFor="opp-lead">Lead rating</label>
        <select
          id="opp-lead"
          value={filters.minLeadScore}
          onChange={(event) => onChange({ ...filters, minLeadScore: event.target.value })}
        >
          <option value="">Any</option>
          <option value="9">9 and above</option>
          <option value="7">7 and above</option>
          <option value="5">5 and above</option>
          <option value="3">3 and above</option>
        </select>
      </div>

      <TagFilter
        id="opp-tags"
        options={tagOptions}
        selected={filters.tags}
        match={filters.tagMatch}
        onChange={(tags, tagMatch) => onChange({ ...filters, tags, tagMatch })}
      />

      <SwitchField
        id="opp-deleted"
        label="Deleted"
        text="Include deleted"
        checked={filters.includeDeleted}
        onChange={(includeDeleted) => onChange({ ...filters, includeDeleted })}
      />
    </>
  );

  return (
    <FilterPanel actions={views}
      activeCount={activeCount + moreActiveCount}
      moreActiveCount={moreActiveCount}
      more={more}
      onReset={onReset}
    >
      <DateRangeField
        id="opp-from"
        label="Created between"
        from={filters.from}
        to={filters.to}
        max={today}
        presets={PRESETS.map((preset) => ({ label: preset.label, from: daysAgo(preset.days), to: today }))}
        onChange={(from, to) => onChange({ ...filters, from, to })}
      />

      <SwitchField
        id="opp-active"
        label="Clients"
        text="Active only"
        checked={filters.activeClientsOnly}
        onChange={(checked) => onChange({ ...filters, activeClientsOnly: checked, locationIds: [] })}
      />

      <div className="field">
        <label htmlFor="opp-location">Sub-account</label>
        <select
          id="opp-location"
          value={filters.locationIds[0] ?? ''}
          onChange={(event) =>
            onChange({
              ...filters,
              locationIds: event.target.value ? [event.target.value] : [],
              userIds: [],
              pipelineIds: [],
              stageIds: [],
            })
          }
        >
          <option value="">
            {filters.activeClientsOnly ? 'All active clients' : 'All sub-accounts'}
          </option>
          {visibleLocations.map((location) => (
            <option value={location.id} key={location.id}>
              {location.name}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="opp-pipeline">Pipeline</label>
        <select
          id="opp-pipeline"
          value={selectedPipelineKey}
          onChange={(event) =>
            onChange({
              ...filters,
              pipelineIds:
                pipelineGroups.find((group) => group.key === event.target.value)?.ids ?? [],
              stageIds: [],
            })
          }
        >
          <option value="">All pipelines</option>
          {pipelineGroups.map((group) => (
            <option value={group.key} key={group.key}>
              {group.key}
              {group.ids.length > 1 ? ` (${group.ids.length} sub-accounts)` : ''}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="opp-stage">Stage</label>
        <select
          id="opp-stage"
          value={selectedStageKey}
          onChange={(event) =>
            onChange({
              ...filters,
              stageIds: stageGroups.find((group) => group.key === event.target.value)?.ids ?? [],
            })
          }
        >
          <option value="">All stages</option>
          {stageGroups.map((group) => (
            <option value={group.key} key={group.key}>
              {group.key}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="opp-user">Owner</label>
        <select
          id="opp-user"
          value={filters.userIds[0] ?? ''}
          onChange={(event) => onChange({ ...filters, userIds: event.target.value ? [event.target.value] : [] })}
        >
          <option value="">Anyone</option>
          <option value="unassigned">Unassigned</option>
          {people
            .filter((person) => person.userId)
            .map((person) => (
              <option value={person.userId!} key={`${person.userId}-${person.locationId}`}>
                {person.userName ?? person.userId} ({person.total})
              </option>
            ))}
        </select>
      </div>

      <div className="field">
        <label>Status</label>
        <div className="chips">
          {OPPORTUNITY_STATUSES.map((status) => (
            <button
              type="button"
              className="chip"
              key={status}
              aria-pressed={filters.statuses.includes(status)}
              onClick={() => toggleStatus(status)}
            >
              <span
                className="swatch"
                style={{ background: OPPORTUNITY_STATUS_COLORS[status] }}
                aria-hidden="true"
              />
              {OPPORTUNITY_STATUS_LABELS[status]}
            </button>
          ))}
        </div>
      </div>

      <div className="field">
        <label htmlFor="opp-sort">Sort by</label>
        <select
          id="opp-sort"
          value={filters.sort}
          onChange={(event) =>
            onChange({ ...filters, sort: event.target.value as OpportunityFilterState['sort'] })
          }
        >
          <option value="created">Newest first</option>
          <option value="lastAppointment">Last appointment</option>
          <option value="nextAppointment">Next appointment</option>
          <option value="value">Highest value</option>
          <option value="leadScore">Highest lead rating</option>
        </select>
      </div>

      <div className="field field-grow">
        <label htmlFor="opp-search">Search</label>
        <input
          id="opp-search"
          type="search"
          placeholder="Opportunity or contact…"
          value={filters.search}
          onChange={(event) => onChange({ ...filters, search: event.target.value })}
        />
      </div>
    </FilterPanel>
  );
}

/** Groups items sharing a display name; `ids` is what the filter sends. */
function groupByName<T extends { id: string }>(
  items: T[],
  nameOf: (item: T) => string,
): { key: string; ids: string[]; items: T[] }[] {
  const groups = new Map<string, { key: string; ids: string[]; items: T[] }>();
  for (const item of items) {
    const key = nameOf(item).trim() || item.id;
    const group = groups.get(key) ?? { key, ids: [], items: [] };
    group.ids.push(item.id);
    group.items.push(item);
    groups.set(key, group);
  }
  return [...groups.values()].sort((a, b) => a.key.localeCompare(b.key));
}
